import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useSupabase } from "@/auth/SupabaseProvider";

import { apiFetch } from "./client";
import type { AgentConfigResponse, AgentConfigUpdateResponse } from "./types";

/**
 * TanStack Query hook for the `/settings` page. Mirrors `useCatalogue`
 * — single-fetch read, no auto-refresh on focus. Operators trigger a
 * refresh by re-navigating to the route or hitting reload.
 *
 * Phase 2 (#75) adds `useUpdateAgentConfig` below; the page-level Save
 * button fans out a per-dirty-row mutation through it. On any
 * mutation success we invalidate this query so the next read pulls
 * the live Sheet's freshly-written values.
 */
export function useAgentConfig() {
  const { client } = useSupabase();
  return useQuery({
    queryKey: ["agent-config"],
    queryFn: ({ signal }) =>
      apiFetch<AgentConfigResponse>(client, "/api/agent-config", { signal }),
  });
}

/**
 * Single-key write mutation hook. The page orchestrates the per-row
 * fan-out via `Promise.allSettled`, so the hook itself is deliberately
 * minimal — one `(key, value)` per call. Invalidates the GET cache on
 * success so the post-save refetch picks up everything that landed.
 *
 * Errors propagate to the caller verbatim (the page maps them onto
 * per-row chips). The hook does not retry — the operator can hit Save
 * again to retry the still-dirty rows.
 */
export function useUpdateAgentConfig() {
  const { client } = useSupabase();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ key, value }: { key: string; value: string | number }) =>
      apiFetch<AgentConfigUpdateResponse>(
        client,
        `/api/agent-config/${encodeURIComponent(key)}`,
        { method: "PUT", body: { value } },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["agent-config"] });
    },
  });
}
